import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/free-solid-svg-icons';
import SidebarPageLayout from '../../components/layout/SidebarPageLayout';
import Breadcrumbs from '../../components/layout/Breadcrumbs';
import { useAuth } from '../../components/auth/AuthProvider';
import { useSessionData } from '../../components/data/SessionData';
import { updateProfile } from '../../api/auth';
import { appRoleById, canViewOrganizationSettings } from '../../auth/appRoles';
import { toSlug } from '../../util/strings';

function displayValue(value) {
  const text = String(value ?? '').trim();
  return text || '—';
}

function formatTimestamp(value, withTime) {
  if (value == null || value === '') {
    return '';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  const options = {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  };
  if (withTime) {
    options.hour = 'numeric';
    options.minute = '2-digit';
  }
  return parsed.toLocaleString('en-US', options);
}

function Account() {
  const { user, isAuthLoading, patchUser } = useAuth();
  const { sites } = useSessionData();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    document.title = 'Vivosol Plant Performance Insights Hub | Admin / Account';
  }, []);

  useEffect(() => {
    setFirstName(String(user?.first_name ?? ''));
    setLastName(String(user?.last_name ?? ''));
  }, [user?.first_name, user?.last_name]);

  async function handleUpdateProfile(event) {
    event.preventDefault();
    setSaving(true);
    setSaveError('');
    setSaved(false);

    try {
      const data = await updateProfile({ firstName, lastName });
      const nextFirst = String(data?.user?.first_name ?? '').trim();
      const nextLast = String(data?.user?.last_name ?? '').trim();
      setFirstName(nextFirst);
      setLastName(nextLast);
      patchUser({ first_name: nextFirst, last_name: nextLast });
      setSaved(true);
    } catch (error) {
      console.warn('Profile update failed:', error);
      setSaveError('Unable to update profile.');
    } finally {
      setSaving(false);
    }
  }

  const role = appRoleById(user?.role_id);
  const showOrganizationSettings = canViewOrganizationSettings(user?.role_id);
  const organization = String(user?.organization ?? '').trim();
  const createdLabel = formatTimestamp(user?.created_at, false);
  const lastLoginLabel = formatTimestamp(user?.last_login, true);
  const siteRecords = Array.isArray(sites) ? sites : [];

  return (
    <SidebarPageLayout id="adminAccount">
      <div className="content p-5">
        <h1 className="mb-0">My Account</h1>
        <Breadcrumbs />
        <div className="panel p-3">
          <div className="header d-flex justify-content-between pb-3">
            <div className="left d-flex gap-3">
              <span><FontAwesomeIcon icon={faUser} /> PROFILE</span>
            </div>
          </div>
          <div className="body pt-3">
            {isAuthLoading ? (
              <p className="text-muted mb-0">Loading profile…</p>
            ) : (
              <>
                <form className="form account-profile d-flex flex-column" onSubmit={handleUpdateProfile}>
                  <div className="form-group">
                    <span className="form-label">Email</span>
                    <div className="form-control-plaintext account-email">{displayValue(user?.email)}</div>
                  </div>
                  <div className="form-group">
                    <span className="form-label">First Name</span>
                    <input
                      type="text"
                      className="form-control"
                      value={firstName}
                      onChange={(event) => setFirstName(event.target.value)}
                      aria-label="First Name"
                    />
                  </div>
                  <div className="form-group">
                    <span className="form-label">Last Name</span>
                    <input
                      type="text"
                      className="form-control"
                      value={lastName}
                      onChange={(event) => setLastName(event.target.value)}
                      aria-label="Last Name"
                    />
                  </div>
                  <div className="form-group">
                    <span className="form-label">Organization</span>
                    <div className="form-control-plaintext account-organization">
                      {organization && showOrganizationSettings ? (
                        <Link className="account-org-link" to="/admin/settings#organization">{organization}</Link>
                      ) : displayValue(organization)}
                    </div>
                  </div>
                  <div className="form-group">
                    <span className="form-label">Role</span>
                    <div className="form-control-plaintext account-role">
                      {role ? (
                        <Link to="/help/documentation/roles">{role.label}</Link>
                      ) : '—'}
                      {role?.id === 2 && organization ? (
                        <Link className="account-org-link" to="/admin/settings#organization">
                          ({organization} administrator)
                        </Link>
                      ) : null}
                    </div>
                  </div>
                  <div className="form-group">
                    <span className="form-label">Sites</span>
                    <div className="form-control-plaintext account-sites">
                      {siteRecords.length ? (
                        siteRecords.map((site, index) => {
                          const siteName = String(site?.name || '').trim();
                          const slug = toSlug(site?.slug || siteName);
                          return (
                            <span key={slug || siteName}>
                              {index > 0 ? ', ' : null}
                              {slug ? (
                                <Link to={`/portfolio/dashboard/${slug}`}>{siteName}</Link>
                              ) : siteName}
                            </span>
                          );
                        })
                      ) : '—'}
                    </div>
                  </div>
                  <div className="form-group">
                    <span className="form-label">Last Login</span>
                    <div className="form-control-plaintext account-last-login">{lastLoginLabel || '—'}</div>
                  </div>
                  <div className="form-group">
                    <span className="form-label">Created</span>
                    <div className="form-control-plaintext account-created">{createdLabel || '—'}</div>
                  </div>
                  <div className="form-group account-actions">
                    <span className="form-label" aria-hidden="true" />
                    <div className="account-update-wrap">
                      <button type="submit" className="account-update" disabled={saving}>
                        Update Profile
                      </button>
                      {saving ? (
                        <span className="account-update-spinner" role="status" aria-label="Updating profile" />
                      ) : null}
                      {saveError ? <p className="account-save-error mb-0">{saveError}</p> : null}
                      {saved ? <p className="account-save-ok mb-0">Profile Updated</p> : null}
                    </div>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </SidebarPageLayout>
  );
}

export default Account;
